import React, { useCallback, useState, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
type ToastType = 'success' | 'error' | 'info';
interface Toast {
  id: string;
  message: string;
  type: ToastType;
}
interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((message: string, type: ToastType) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-1323">
      {children}
      <div className="fixed top-4 left-0 right-0 z-50 flex flex-col items-center gap-2 pointer-events-none px-4" data-id="element-1324">
        {toasts.map(toast => <div key={toast.id} className={`
              pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg w-full max-w-sm transform transition-all duration-300 animate-in slide-in-from-top-5 fade-in
              ${toast.type === 'success' ? 'bg-green-50 text-green-900 border border-green-200' : ''}
              ${toast.type === 'error' ? 'bg-red-50 text-red-900 border border-red-200' : ''}
              ${toast.type === 'info' ? 'bg-blue-50 text-blue-900 border border-blue-200' : ''}
            `} data-id="element-1325">
            {toast.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600" data-id="element-1326" />}
            {toast.type === 'error' && <AlertCircle className="w-5 h-5 text-red-600" data-id="element-1327" />}
            {toast.type === 'info' && <Info className="w-5 h-5 text-blue-600" data-id="element-1328" />}

            <p className="flex-1 text-sm font-medium" data-id="element-1329">{toast.message}</p>

            <button onClick={() => removeToast(toast.id)} className="p-1 hover:bg-black/5 rounded-full transition-colors" data-id="element-1330">
              <X className="w-4 h-4 opacity-50" data-id="element-1331" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}